import React, { useState } from 'react';
import { Calendar, ChevronDown, ChevronUp, Droplets, Wrench } from 'lucide-react';
import {
  getAllMilestonesForecast,
  type MilestoneProgress,
} from '../../services/scheduler';
import { StatusBadge } from './StatusBadge';

export interface ServiceForecastProps {
  cumulativeHours: number;
  lastServiceHours?: number;
  avgDailyHours?: number;
  initiallyExpanded?: boolean;
  className?: string;
}

export function ServiceForecast({
  cumulativeHours,
  lastServiceHours = 0,
  avgDailyHours = 16,
  initiallyExpanded = false,
  className = '',
}: ServiceForecastProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(initiallyExpanded);

  const milestones: MilestoneProgress[] = getAllMilestonesForecast(
    cumulativeHours,
    lastServiceHours,
    avgDailyHours
  );

  return (
    <div className={`bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs ${className}`}>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 bg-white hover:bg-slate-50 flex items-center justify-between text-left transition-colors min-h-[48px]"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-bold text-slate-900">
            แผนพยากรณ์การบำรุงรักษาตามชั่วโมงทำงาน (Service Milestone Forecast)
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>{milestones.length} ระดับชั่วโมง</span>
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 border-t border-slate-100 space-y-3 bg-slate-50/50">
          <p className="text-xs text-slate-500">
            พยากรณ์กำหนดเวลาโดยประมาณโดยอิงจากอัตราการใช้งานเฉลี่ย{' '}
            <strong className="text-slate-800 font-semibold">{avgDailyHours} ชม./วัน</strong> (2 กะทำงาน)
          </p>

          <div className="space-y-2.5">
            {milestones.map((m) => {
              const formattedDate = new Date(m.estimatedDueDate).toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              });

              return (
                <div
                  key={m.tier}
                  className={`p-3 rounded-lg border transition-all ${
                    m.status === 'OVERDUE'
                      ? 'bg-rose-50 border-rose-200'
                      : m.status === 'DUE_SOON'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-white border-slate-200 shadow-2xs'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm font-mono text-slate-900">
                          {m.tier.toLocaleString()} ชม.
                        </span>
                        <StatusBadge status={m.status} size="sm" showPulse={false} />
                      </div>
                      <p className="text-xs font-semibold text-slate-800 mt-1">
                        {m.description}
                      </p>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <span className="text-xs font-mono font-bold text-slate-800">
                        {m.progressPercent.toFixed(1)}%
                      </span>
                      <p className="text-[11px] text-slate-500">
                        {m.remainingHours > 0
                          ? `เหลืออีก ${m.remainingHours.toFixed(1)} ชม.`
                          : `เกิน ${Math.abs(m.remainingHours).toFixed(1)} ชม.`}
                      </p>
                    </div>
                  </div>

                  {/* Mini Progress Bar */}
                  <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full ${
                        m.status === 'OVERDUE'
                          ? 'bg-rose-500'
                          : m.status === 'DUE_SOON'
                          ? 'bg-amber-500'
                          : 'bg-emerald-500'
                      }`}
                      style={{ width: `${m.progressPercent}%` }}
                    />
                  </div>

                  {/* Lubricants & Estimated Due Date */}
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] pt-1 border-t border-slate-100 text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      <span>
                        ประมาณการ:{' '}
                        <strong className="text-slate-800 font-semibold">
                          {m.remainingHours > 0 ? `${formattedDate} (~${m.daysRemaining} วัน)` : 'เลยกำหนด'}
                        </strong>
                      </span>
                    </div>

                    {m.lubricants && m.lubricants.length > 0 && (
                      <div className="flex items-center gap-1 text-emerald-700 font-semibold">
                        <Droplets className="w-3 h-3" />
                        <span>{m.lubricants.map((l) => l.code).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
